<template>
  <div class="host-portal-container">
    <!-- Loading state -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>{{ t('hostPortalPage.loadingEventDetails') }}</p>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="error-message">
      <h2>{{ t('hostPortalPage.genericErrorTitle') }}</h2>
      <p>{{ error }}</p>
      <button @click="retryLoading" class="retry-button">{{ t('hostPortalPage.tryAgain') }}</button>
    </div>

    <!-- Content when loaded successfully -->
    <div v-else>
      <!-- Header with Event Title -->
      <div class="event-header">
        <h1 class="event-title">{{ event.title || t('hostPortalPage.eventTitleFallback', { name: `${event.firstName} ${event.lastName}` }) }}</h1>
        <p class="event-subtitle">{{ t('hostPortalPage.eventSubtitle') }}</p>
      </div>

    <!-- Event Images Gallery -->
    <div class="event-images">
      <div
        v-for="(image, index) in eventImages"
        :key="index"
        class="event-image-container"
        :class="{ 'event-image-container--middle': index === 1 }"
      >
        <img :src="image" :alt="t('hostPortalPage.eventImageAlt', { number: index + 1 })" class="event-image">
      </div>
    </div>

    <!-- Event Date Display -->
    <div class="event-date-display">
      <div class="day">{{ eventDay }}</div>
      <div class="month">{{ eventMonth }}</div>
    </div>

    <!-- Event Details Section -->
    <div class="event-details-container">
      <!-- Left Column - Event Description -->
      <div class="event-description-column">
        <h2 class="description-title">{{ t('hostPortalPage.descriptionTitle') }}</h2>
        <p class="host-info">{{ t('hostPortalPage.hostedBy') }}: {{ event.hostName || `${event.firstName} ${event.lastName}` }}</p>
        <div class="event-location" v-if="event.eventCity || event.eventState">
          <p><strong>{{ t('hostPortalPage.location') }}:</strong> {{ formatEventLocation() }}</p>
        </div>
        <p class="event-description">{{ t('hostPortalPage.eventDescription') }}</p>

        <!-- Shop Now Button -->
        <button class="shop-now-btn" @click="shopNow">{{ t('hostPortalPage.shopNow') }}</button>
      </div>

      <!-- Right Column - Sharing Options -->
      <div class="event-sharing-column">
        <!-- Guest RSVP Section (only when ?eventContactId=... is present in URL) -->
        <div v-if="guest" class="guest-rsvp-card">
          <h3 class="guest-rsvp-heading">{{ t('hostPortalPage.guestSection.heading') }}</h3>
          <p class="guest-rsvp-greeting">
            {{ t('hostPortalPage.guestSection.greeting', { name: guest.name || `${guest.firstName ?? ''} ${guest.lastName ?? ''}`.trim() }) }}
          </p>
          <div class="guest-rsvp-current">
            <span class="guest-rsvp-label">{{ t('hostPortalPage.guestSection.currentStatusLabel') }}</span>
            <span class="guest-rsvp-value">{{ guest.codeTextInvitationStatus || '—' }}</span>
          </div>
          <div class="guest-rsvp-change">
            <label for="guest-rsvp-status" class="guest-rsvp-label">
              {{ t('hostPortalPage.guestSection.changeStatusLabel') }}
            </label>
            <select
              id="guest-rsvp-status"
              v-model="selectedStatusId"
              class="guest-rsvp-select"
              :disabled="isUpdatingStatus"
              @change="updateInvitationStatus"
            >
              <option
                v-for="opt in invitationStatuses"
                :key="opt.id"
                :value="opt.id"
              >{{ opt.text }}</option>
            </select>
          </div>
        </div>

        <h3 class="share-title">{{ t('hostPortalPage.shareThisInvite') }}</h3>

        <div class="sharing-options">
          <div class="share-option" @click="copyInviteLink">
            <div class="share-icon copy-icon"></div>
            <span>{{ t('hostPortalPage.copy') }}</span>
          </div>

          <!-- Event Start Time -->
          <div class="event-start-time">
            <div class="calendar-icon"></div>
            <span>{{ t('hostPortalPage.starts') }} {{ formattedEventDate }}</span>
            <div v-if="event.eventDate" class="event-time">
              {{ formatEventTime(event.eventDate) }}
            </div>
          </div>
        </div>

        <!-- Host Dashboard Link -->
        <div class="host-dashboard-link" v-if="isEventHost">
          <div class="dashboard-icon"></div>
          <p>{{ t('hostPortalPage.hostingQuestion') }}</p>
          <a href="#" @click.prevent="goToDashboard">{{ t('hostPortalPage.signInToSeeDashboard') }}</a>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '@/logger.ts'
import type { FilterContainer } from '@/types/FilterTypes'
import Swal from 'sweetalert2'

import { GetEventHosts, EventGuestGetList, EventGuestUpdate } from '@/services/EventService'
import { GetShopUrl, GetImageRootUrl } from '@/stores/SettingsStore'
import { getDateFormatLocale } from '@/api/Common'
import type { IEvent,IEventHostResponse,IEventHost,IEventGuest } from '@/types/EventTypes'
import type { IAddress } from '@/types/CommonTypes'
import { SetIsLoading } from '@/services/AoeHelpers.ts'
import { GetCodesFromLocalStorageOrApi } from '@/services/CountryCultureService'
import type { ICategoryCode } from '@/types/CategoryCodeTypes'
import { handleError } from '@/services/ErrorService'
import { sanitizeLocationPart } from '@/utils/locationHelper'

const { t } = useI18n()

// Define an event interface to match the available API data structure
interface IExtendedEvent {
  eventId: number;
  firstName?: string;
  lastName?: string;
  eventDate: string;
  eventCity?: string;
  eventState?: string;
  eventCountry?: string;
  consultantBeeNumber?: number;
  // Derived properties
  hostName?: string;
  title?: string;
  eventStartDate?: string;
  eventEndDate?: string;
}

const logger = useLogger()
setStoreLogger(logger)

// Since vue-router is not available, we'll parse the URL manually.
// Keys are normalized to lowercase so the component works regardless of
// hosting-level URL rewriting (e.g. Umbraco's ShouldForceUrlsToLowerCase).
function getQueryParams() {
  const params = new URLSearchParams(window.location.search)
  const result: Record<string, string> = {}
  for (const [key, value] of params.entries()) {
    result[key.toLowerCase()] = value
  }
  return result
}

// Format location from event data
function formatEventLocation(): string {
  const parts = [];
  if (event.value.eventCity) parts.push(event.value.eventCity);
  const eventState = sanitizeLocationPart(event.value.eventState);
  if (eventState) parts.push(eventState);
  if (event.value.eventCountry) parts.push(event.value.eventCountry);

  return parts.join(', ');
}

// Format event time from ISO string
function formatEventTime(dateTimeString: string): string {
  if (!dateTimeString) return '';

  const date = new Date(dateTimeString);
  return date.toLocaleTimeString(getDateFormatLocale(), {
    hour: '2-digit',
    minute: '2-digit'
  });
}

const event = ref<IExtendedEvent>({} as IExtendedEvent)
const isLoading = ref(true)
const error = ref<string | null>(null)
const isEventHost = ref(false)

// --- Guest RSVP state ---
const eventIdToken = ref<string>('')
const guest = ref<IEventGuest | null>(null)
const invitationStatuses = ref<ICategoryCode[]>([])
const selectedStatusId = ref<string>('')
const isUpdatingStatus = ref<boolean>(false)

// Shop (guest cart) URL — loaded once on mount and used to build the Shop Now link.
// Mirrors the `cartPageUrl` computed in AoeEventManagement.vue.
const cartDetailURL = ref<string>('')

const cartPageUrl = computed(() => {
  if (cartDetailURL.value && eventIdToken.value) {
    let baseUrl = cartDetailURL.value
    // If not absolute, prepend window.location.origin
    if (!/^https?:\/\//i.test(baseUrl)) {
      baseUrl = window.location.origin + (baseUrl.startsWith('/') ? '' : '/') + baseUrl
    }
    return baseUrl + '?event=' + eventIdToken.value
  }
  return ''
})

// Event images using imageRootUrl from settings + i18n keys
const eventImages = computed(() => {

  return [
    `${imageRoot.value}/contentimages/${t('hostDashboard.HostPortalImage1')}`,
    `${imageRoot.value}/contentimages/${t('hostDashboard.HostPortalImage2')}`,
    `${imageRoot.value}/contentimages/${t('hostDashboard.HostPortalImage3')}`
  ]
})

// Format event date.
// Uses Intl.DateTimeFormat.formatToParts so we extract the actual month/day parts
// regardless of locale ordering or punctuation. The previous implementation split
// the formatted string on spaces, which broke for locales like de-DE where the
// day comes first followed by a period (e.g. "9. Sept. 2025" -> month became "9.").
const eventMonth = computed(() => {
  if (!event.value.eventDate) return 'FEB'
  const date = new Date(event.value.eventDate)
  const parts = new Intl.DateTimeFormat(getDateFormatLocale(), { month: 'short' }).formatToParts(date)
  const monthPart = parts.find(p => p.type === 'month')?.value ?? ''
  return monthPart.replace(/\.$/, '').toUpperCase()
})

const eventDay = computed(() => {
  if (!event.value.eventDate) return '1'
  const date = new Date(event.value.eventDate)
  const parts = new Intl.DateTimeFormat(getDateFormatLocale(), { day: 'numeric' }).formatToParts(date)
  return parts.find(p => p.type === 'day')?.value ?? date.getDate().toString()
})

const formattedEventDate = computed(() => {
  if (!event.value.eventDate) return 'Saturday, 1 February'
  const date = new Date(event.value.eventDate)
  return date.toLocaleDateString(getDateFormatLocale(), { weekday: 'long', day: 'numeric', month: 'long' })
})

// Fetch event data on component mount
const imageRoot = ref('')
onMounted(async () => {
  try {
    const params = getQueryParams()
    // Accept either ?eventIdToken=... (preferred, token-based) or ?eventId=...
    // (legacy, plain numeric id). Token takes precedence when both are present.
    const eventIdTokenParam = params.eventidtoken
    const eventId = params.eventid
    const eventContactId = params.eventcontactid
    if (!eventIdTokenParam && !eventId) {
      error.value = t('hostPortalPage.errors.noEventIdProvided')
      isLoading.value = false
      return
    }
     imageRoot.value = await GetImageRootUrl()

    // Kick off invitation status code load in parallel — only needed if a guest
    // is in the URL, but loading early avoids a second wait when the guest data arrives.
    void loadInvitationStatuses()

    // Load the shop (guest cart) URL so the Shop Now button can build a link.
    GetShopUrl()
      .then(url => { cartDetailURL.value = url })
      .catch(e => logger.error('Failed to load shop URL', e))

    // EventIdToken is not a real DB column on VSfaEventHostListEntity, so we can't
    // filter by it directly. The numeric EventId is the portion of the token before
    // the first '.' (e.g. "40040.abc123" -> 40040), so extract it for the filter.
    const resolvedEventId = eventIdTokenParam
      ? eventIdTokenParam.split('.')[0]
      : eventId
    const filterContainer: FilterContainer = {
      Where: {
        OperatorType: "Or",
        Operands: [
          { Field: "EventId", Value: resolvedEventId, FilterType: "Equal" }
        ]
      }
    };

    const request = {
      responseFilter: filterContainer
    };

    try {
      const eventHostResponse: IEventHostResponse = await GetEventHosts(request)
      logger.debug('Event host search results:', eventHostResponse)

      if (eventHostResponse?.items && eventHostResponse.items.length > 0) {
        const hostData = eventHostResponse.items[0]
        const hostName = `${hostData.firstName || ''} ${hostData.lastName || ''}`.trim() || t('hostPortalPage.unknownHost')

        eventIdToken.value = hostData.eventIdToken || ''

        event.value = {
          eventId: hostData.eventId,
          firstName: hostData.firstName,
          lastName: hostData.lastName,
          eventDate: hostData.eventDate,
          eventCity: hostData.eventCity,
          eventState: hostData.eventState,
          eventCountry: hostData.eventCountry,
          consultantBeeNumber: hostData.consultantBeeNumber,
          hostName: hostName,
          title: `${hostName}'s Event`,
          eventStartDate: hostData.eventDate,
          eventEndDate: hostData.eventDate
        }

        logger.debug('Processed event data:', event.value)

        // If the URL also contains an eventContactId, load that specific guest.
        if (eventContactId && eventIdToken.value) {
          await loadGuest(eventContactId)
        }
      } else {
        error.value = t('hostPortalPage.errors.eventNotFound')
      }
    } catch (err) {
      logger.error('Error searching for events:', err)
      error.value = t('hostPortalPage.errors.loadingEventDetails')
    } finally {
      isLoading.value = false
    }
  } catch (err) {
    logger.error('Error fetching event:', err);
    error.value = t('hostPortalPage.errors.loadingEventDetails')
    isLoading.value = false;
  }
})

// Load the invitation status category codes (used by the RSVP dropdown).
async function loadInvitationStatuses() {
  try {
    const data = await GetCodesFromLocalStorageOrApi('INVITATIONSTATUS')
    if (data?.items) {
      invitationStatuses.value = data.items
        .map(code => ({ text: code.codeText, id: code.codeId }))
        .sort((a, b) => a.text.localeCompare(b.text))
    }
  } catch (err) {
    logger.error('Failed to load invitation statuses:', err)
  }
}

// Look up a single guest by eventContactId from the event's guest list.
async function loadGuest(eventContactIdRaw: string) {
  const eventContactId = Number(eventContactIdRaw)
  if (!eventContactId || Number.isNaN(eventContactId)) return

  try {
    const guestList = await EventGuestGetList({ eventIdToken: eventIdToken.value })
    const match = guestList?.items?.find(g => g.eventContactId === eventContactId) ?? null
    if (match) {
      guest.value = match
      selectedStatusId.value = match.codeIdInvitationStatus || ''
    } else {
      // Show a non-blocking inline message rather than failing the whole page.
      logger.warn('Guest not found for eventContactId:', eventContactId)
      await Swal.fire({
        text: t('hostPortalPage.errors.guestNotFound'),
        icon: 'info',
        toast: true,
        position: 'top-end',
        timer: 3000,
        showConfirmButton: false,
      })
    }
  } catch (err) {
    logger.error('Failed to load guest:', err)
    await Swal.fire({
      text: t('hostPortalPage.errors.loadingGuestDetails'),
      icon: 'error',
    })
  }
}

// Persist the guest's invitation status change.
async function updateInvitationStatus() {
  if (!guest.value || !eventIdToken.value || !selectedStatusId.value) return
  if (selectedStatusId.value === guest.value.codeIdInvitationStatus) return

  isUpdatingStatus.value = true
  try {
    SetIsLoading(true)
    await EventGuestUpdate({
      eventIdToken: eventIdToken.value,
      eventContactId: guest.value.eventContactId,
      codeIdInvitationStatus: selectedStatusId.value,
    })

    // Update local view of the guest so the "current status" label reflects the change.
    const newLabel = invitationStatuses.value.find(s => s.id === selectedStatusId.value)?.text ?? ''
    guest.value = {
      ...guest.value,
      codeIdInvitationStatus: selectedStatusId.value,
      codeTextInvitationStatus: newLabel,
    }

    await Swal.fire({
      title: t('hostPortalPage.guestSection.statusUpdatedTitle'),
      text: t('hostPortalPage.guestSection.statusUpdatedMessage'),
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
      position: 'top-end',
      toast: true,
    })
  } catch (err) {
    logger.error('Failed to update invitation status:', err)
    // Roll back the dropdown selection so it reflects what's actually saved.
    selectedStatusId.value = guest.value.codeIdInvitationStatus || ''
    await handleError(err, t)
  } finally {
    SetIsLoading(false)
    isUpdatingStatus.value = false
  }
}

// Action functions
function shopNow() {
  logger.debug('Shop Now clicked')
  if (!cartPageUrl.value) {
    logger.error('Cannot build shop-now URL — cartPageUrl is empty', {
      cartDetailURL: cartDetailURL.value,
      eventIdToken: eventIdToken.value,
    })
    return
  }
  window.location.href = cartPageUrl.value
}

function shareViaText() {
  logger.debug('Share via Text clicked')
  // Implement text sharing
}

function shareViaFacebook() {
  logger.debug('Share via Facebook clicked')
  // Implement Facebook sharing
}

function shareViaEmail() {
  logger.debug('Share via Email clicked')
  // Implement email sharing
}

function copyInviteLink() {
  logger.debug('Copy invite link clicked')
  // Implement copy to clipboard
  const url = window.location.href
  navigator.clipboard.writeText(url)
    .then(async () => {
      await Swal.fire({
        title: t('hostPortalPage.inviteLinkCopiedTitle'),
        text: t('hostPortalPage.inviteLinkCopiedMessage'),
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
        position: 'top-end',
        toast: true,
      })
    })
    .catch(err => {
      logger.error('Failed to copy invite link:', err)
      Swal.fire({
        text: t('hostPortalPage.errors.copyInviteLinkFailed'),
        icon: 'error',
      })
    })
}

function goToDashboard() {
  logger.debug('Go to dashboard clicked')
  // Implement dashboard navigation
}

// Function to retry loading the event data
function retryLoading() {
  logger.debug('Retrying to load event data')
  isLoading.value = true
  error.value = null

  // Re-trigger the loading process directly
  setTimeout(() => {
    const params = getQueryParams()
    const eventIdTokenParam = params.eventidtoken
    const eventId = params.eventid
    if (!eventIdTokenParam && !eventId) {
      error.value = t('hostPortalPage.errors.noEventIdProvided')
      isLoading.value = false
      return
    }

    // EventIdToken is not a real DB column on VSfaEventHostListEntity, so we can't
    // filter by it directly. The numeric EventId is the portion of the token before
    // the first '.' (e.g. "40040.abc123" -> 40040), so extract it for the filter.
    const resolvedEventId = eventIdTokenParam
      ? eventIdTokenParam.split('.')[0]
      : eventId
    const filterContainer: FilterContainer = {
      Where: {
        OperatorType: "Or",
        Operands: [
          { Field: "EventId", Value: resolvedEventId, FilterType: "Equal" }
        ]
      }
    };

    const request = {
      responseFilter: filterContainer
    };

    GetEventHosts(request).then((eventHostResponse: IEventHostResponse) => {
      if (eventHostResponse && eventHostResponse.items && eventHostResponse.items.length > 0) {
        const hostData = eventHostResponse.items[0];
        const hostName = `${hostData.firstName || ''} ${hostData.lastName || ''}`.trim() || t('hostPortalPage.unknownHost');

        eventIdToken.value = hostData.eventIdToken || ''

        event.value = {
          eventId: hostData.eventId,
          firstName: hostData.firstName,
          lastName: hostData.lastName,
          eventDate: hostData.eventDate,
          eventCity: hostData.eventCity,
          eventState: hostData.eventState,
          eventCountry: hostData.eventCountry,
          consultantBeeNumber: hostData.consultantBeeNumber,
          hostName: hostName,
          title: `${hostName}'s Event`,
          eventStartDate: hostData.eventDate,
          eventEndDate: hostData.eventDate
        };

        // If retrying with a guest in URL, re-load that guest too.
        const eventContactId = params.eventcontactid
        if (eventContactId && eventIdToken.value) {
          void loadGuest(eventContactId)
        }
      } else {
        error.value = t('hostPortalPage.errors.eventNotFound')
      }
    }).catch((err: Error) => {
      error.value = t('hostPortalPage.errors.loadingEventDetails')
    }).finally(() => {
      isLoading.value = false;
    });
  }, 100);
}
</script>

<style lang="scss" scoped>
.host-portal-container {
  max-width: 900px;
  margin: 0 auto;
  padding: $card-padding;
  background-color: white;
  box-shadow: 0 2px 10px rgba(black, 0.1);
  font-family: Arial, sans-serif;
  position: relative;
}

/* Header Styles */
.event-header {
  text-align: center;
  margin-bottom: 20px;
}

.event-title {
  @include typography-display;
  @include font-weight-bold;
  margin-bottom: 10px;
  color: #333;
  text-transform:uppercase;
}

.event-subtitle {
  @include typography-body-large;
  color: #555;
}

/* Event Images Gallery */
.event-images {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
  gap: $spacing-2-5;
}

.event-image-container {
  flex: 1;
  height: 150px;
  overflow: hidden;

  &--middle {
    @media (max-width: 768px) {
      display: none;
    }
  }
}

.event-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.event-date-display {
    position: absolute;
    right: 44%;
    top: 263px;
    width: 80px;
    height: 80px;
    background-color: var(--vt-c-white);
    border-radius: $radius-sm;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    box-shadow: 0 2px 5px rgba(black, 0.3)
}

.month {
  @include typography-body;
  @include font-weight-bold;
  color: #1a6e7d;
}

.day {
  @include typography-h2;
  @include font-weight-bold;
  color: #333;
}

/* Event Details Container */
.event-details-container {
  display: flex;
  gap: $spacing-8;
}

/* Left Column - Event Description */
.event-description-column {
  flex: 1;
  padding-right: 20px;
}

.description-title {
  @include typography-h2;
  color: #333;
  margin-bottom: 15px;
}

.host-info {
  @include typography-body;
  @include font-weight-bold;
  margin-bottom: 15px;
}

.event-description {
  @include typography-body;
  color: #555;
  margin-bottom: 20px;
}

.shop-now-btn {
  background-color: #1a6e7d;
  color: white;
  border: none;
  @include typography-body;
  @include font-weight-bold;
  padding: $spacing-3 $spacing-8;
  border-radius: $radius-sm;
  cursor: pointer;
  transition: background-color 0.3s;
}

.shop-now-btn:hover {
  background-color: var(--vt-c-teal-dark);
}

/* Right Column - Sharing Options */
.event-sharing-column {
  flex: 1;
  padding-left: 20px;
  border-left: 1px solid #e9ecef;
}

.share-title {
  @include typography-body-large;
  color: #333;
  margin-bottom: 15px;
}

.sharing-options {
  display: flex;
  align-items: center;
  gap: $spacing-5;
  margin-bottom: 30px;
}

.share-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}

.share-icon {
  width: 40px;
  height: 40px;
  background-color: #e9ecef;
  border-radius: 50%;
  margin-bottom: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.text-icon::before {
  content: "💬";
}

.facebook-icon::before {
  content: "📘";
}

.email-icon::before {
  content: "✉️";
}

.copy-icon::before {
  content: "📋";
}

/* Event Start Time */
.event-start-time {
  display: flex;
  align-items: center;
}

.calendar-icon {
  width: 30px;
  height: 30px;
  background-color: #e9ecef;
  border-radius: $radius-sm;
  margin-right: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.calendar-icon::before {
  content: "📅";
}

/* Host Dashboard Link */
.host-dashboard-link {
  background-color: var(--vt-c-white-soft);
  padding: $spacing-4;
  border-radius: $radius-sm;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.dashboard-icon {
  width: 30px;
  height: 30px;
  background-color: #e9ecef;
  border-radius: 50%;
  margin-bottom: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.dashboard-icon::before {
  content: "🎉";
}

.host-dashboard-link p {
  @include font-weight-bold;
  margin: $spacing-1 0;
}

.host-dashboard-link a {
  color: #1a6e7d;
  text-decoration: none;
}

.host-dashboard-link a:hover {
  text-decoration: underline;
}

/* Guest RSVP card */
.guest-rsvp-card {
  margin-top: $section-margin-sm;
  padding: $card-padding;
  background-color: var(--color-background-soft);
  border: 1px solid var(--color-border);
  border-radius: $card-radius;
}

.guest-rsvp-heading {
  @include typography-h4;
  margin: 0 0 $spacing-2 0;
  color: var(--color-heading);
}

.guest-rsvp-greeting {
  @include typography-body;
  margin: 0 0 $spacing-4 0;
  color: var(--color-text);
}

.guest-rsvp-current,
.guest-rsvp-change {
  display: flex;
  flex-direction: column;
  gap: $spacing-2;
  margin-bottom: $spacing-4;
}

.guest-rsvp-label {
  @include typography-small;
  @include font-weight-semibold;
  color: var(--color-text);
}

.guest-rsvp-value {
  @include typography-body;
  color: var(--color-text);
}

.guest-rsvp-select {
  @include typography-body;
  padding: $spacing-2 $spacing-3;
  border: 1px solid var(--color-border);
  border-radius: $input-radius;
  background-color: var(--color-background);
  color: var(--color-text);
  max-width: 320px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

/* Loading styles */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: $spacing-12 0;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f0f0f0;
  border-top: 5px solid #1a6e7d;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error styles */
.error-message {
  text-align: center;
  padding: $spacing-10 $spacing-5;
  background-color: #fff5f5;
  border-radius: $radius-sm;
  margin: $spacing-5 0;
}

.error-message h2 {
  color: #e53e3e;
  margin-bottom: 10px;
}

.retry-button {
  background-color: #1a6e7d;
  color: white;
  border: none;
  padding: $spacing-2-5 $spacing-5;
  border-radius: $radius-sm;
  @include font-weight-bold;
  margin-top: 20px;
  cursor: pointer;
}

.retry-button:hover {
  background-color: var(--vt-c-teal-dark);
}
.event-time {padding-left:5px;}

/* Responsive adjustments */
@media (max-width: 768px) {
  .event-details-container {
    flex-direction: column;
  }

  .event-description-column,
  .event-sharing-column {
    padding: 0;
    border: none;
  }

  .event-date-display {
    position: relative;
    right: auto;
    top: auto;
    margin: -40px auto 20px;
  }

  .sharing-options {
    justify-content: space-between;
  }
}
</style>
